import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { useContentMap } from '../content.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { MOBILE_UPDATE_TEMPLATES } from '../email-templates/templates.js';
import type { TemplateContext } from '../email-templates/mailto.js';
import { HighSecurityRequired } from '../components/HighSecurityRequired.js';
import {
  CONTACT_NUMBER_CODE_REASON,
  GENERIC_GATEWAY_MESSAGE,
} from '../lib/contact-number-errors.js';

/**
 * Issue #112 — build the inline failure text for the mobile-number Confirm
 * action. Surfaces the gateway's own error Code + Message verbatim (e.g.
 * "73 — Unable to Process Transaction") so the agent sees the real decline
 * reason beside the button and can correct + retry, rather than a transient,
 * detail-free toast (@dylan219 #80/#82/#94 inline-error UX). Tolerates BOTH
 * outcome shapes the BFF returns:
 *
 *  - a 200 with `Success:false` carrying `ResultCode`/`ResultMessage` (the
 *    gateway's `SuccessErrorInd:'N'` business error, mapped by `mapWriteOutcome`
 *    — e.g. ErrorNumber 73 "Unable to Process Transaction");
 *  - a 4xx `gateway_error` body `{ gateway: { code, message } }` (a true HTTP
 *    rejection — e.g. a `1450` payload-validation 400 — decoded by the BFF).
 *
 * `0000` is the internal success code and is never shown. Falls back to a
 * generic line only when the gateway gave neither (e.g. a transport failure).
 *
 * Issue #643 — the real gateway returns these declines with its GENERIC message
 * "Unable to Process Transaction", so a raw `73 — Unable to Process
 * Transaction` was unreadable. When the gateway message is absent or that
 * generic default, substitute the authoritative catalogue reason (op "Amend
 * customer contact number", `CONTACT_NUMBER_CODE_REASON`) so `73` reads
 * "73 — Invalid Mobile Phone No. Supplied"; a genuinely specific gateway
 * message is still preferred and shown verbatim.
 */
export function formatContactUpdateError(body: unknown): string {
  const b = (body ?? {}) as {
    ResultCode?: string;
    ResultMessage?: string;
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (b.gateway?.message ?? b.ResultMessage)?.trim();
  const showCode = code && code !== '0000';
  const reason = code ? CONTACT_NUMBER_CODE_REASON[code] : undefined;
  const messageIsUseful = !!message && !GENERIC_GATEWAY_MESSAGE.test(message);
  const usable = messageIsUseful ? message : (reason ?? message);
  if (showCode && usable) return `${code} — ${usable}`;
  if (usable) return usable;
  if (showCode) return `Mobile number update failed (code ${code}).`;
  return 'Could not update the mobile number. Please retry.';
}

/**
 * SCRN 26 — Change Mobile Number (CCF-6), access-gated behind High Security.
 *
 * Issue #363 (@dylan219, 2026-07-15): during an online interaction the agent
 * must not reach the Mobile Number maintenance screen until **High Security**
 * has been completed for the identified customer. Until then this renders the
 * {@link HighSecurityRequired} block panel ("High Security must be completed to
 * access this journey" + Back + Go to High Security) instead of the form.
 *
 * "High Security complete" is `customer.securityLevel === 'high'` — the highest
 * rung of the client-observed verification ladder (`none → pan → medium →
 * high`), set by the High Security screens (SCRN 4..10) on completion.
 *
 * Issue #412 (@dylan219, 2026-07-17): the block is scoped to the **ONLINE**
 * journey only — "when offline there is no need to block this page and return to
 * High Security". #363's original reasoning assumed this screen is only ever
 * reached online (Customer Search being the offline entry, with no identified
 * customer and hence no `securityLevel`); the bug report (offline agent reaches
 * SCRN-26 and is bounced to High Security) shows that assumption does not hold,
 * so the online scope is now enforced explicitly. Onlineness is read from the
 * authoritative server-resolved session `state` (`GET /api/session`, resolved
 * from the Genesys interaction header — #357), NOT a spoofable client flag.
 * OFFLINE ⇒ the form is rendered directly, no forced High-Security redirect.
 *
 * The BFF write is the authoritative boundary and is UNCHANGED:
 * `POST /api/customers/contact-number` is `requireVerification('high')`-gated
 * server-side (issue #363) AND RBAC online/offline-gated (`updateContactNumber`
 * — offline-write-restricted per #248/#285), so relaxing this client-side block
 * offline opens no write hole: a merely medium-verified or offline-write-denied
 * session is still refused server-side even if it invokes the API directly. This
 * screen-access gate is only the client-side UX affordance (mirroring the #248
 * permission-denied and #222 servicing-verification block pages).
 *
 * Fail-closed while the session state is still resolving: the block is kept
 * (online semantics) until we positively KNOW the session is offline, so a
 * session-fetch hiccup never silently drops #363's online High-Security block.
 */
export function MobileUpdateScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  // Server-resolved Online/Offline state. `null` = not yet known (fail-closed:
  // treat as online, i.e. keep the #363 block until we confirm offline).
  const [interactionState, setInteractionState] = useState<
    'online' | 'offline' | null
  >(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/session');
        if (cancelled || !data) return;
        if (data.state === 'online' || data.state === 'offline') {
          setInteractionState(data.state);
        }
      } catch {
        // Fail-safe: on any error leave `interactionState` null ⇒ online
        // semantics ⇒ the #363 High-Security block stays in force.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // #412: only block + return-to-High-Security on the ONLINE journey. Offline
  // (positively resolved) never blocks. While unresolved, fail closed to the
  // online block. High Security already complete ⇒ never block in either state.
  const isOffline = interactionState === 'offline';
  if (!isOffline && customer.securityLevel !== 'high') {
    return <HighSecurityRequired />;
  }
  return <MobileUpdateForm />;
}

/**
 * SCRN 26 — Change Mobile Number (CCF-6). Faithful port of the
 * update-mobile-update Figma:
 *   1. Floor support / team lead full name (letters only) → Update reveals the
 *      mobile form.
 *   2. New mobile number (numeric, 6–16) → Enter shows it for read-back.
 *   3. "Mobile number confirmed" / "Mobile number incorrect" radios; on
 *      confirmed, Confirm submits to the BFF
 *      (`POST /api/customers/contact-number` → `customer/details/contact-number`)
 *      and shows the 24-hour processing notice + the Visa/TSYS email reminder,
 *      under a "Draft email" HEADING (issue #664 / CCRS-3451 — a section header,
 *      not a button) above the shared "Email templates" dropdown (issue #798 /
 *      CCRS-3841 — {@link EmailTemplateMenu}, matching every other template
 *      screen) that produces the pre-filled Mobile Number Update / SA Accounts
 *      Callback drafts for the Team Leader (a local draft affordance — there is
 *      no Bank gateway endpoint for this).
 *
 * The shared agent banner lives in the app shell, so this renders only `<main>`.
 *
 * Access is gated: {@link MobileUpdateScreen} blocks entry until the customer
 * has passed **High Security** (issue #363); this inner component renders the
 * actual maintenance form and is only reached once that gate passes.
 */
function MobileUpdateForm(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  // #637 inc 13 — the resolved content-catalogue map, threaded into the email
  // template so its editable wording (fixed sentences / subject / field labels)
  // reflects any admin reword; absent-key defaults keep the output unchanged.
  const contentMap = useContentMap();

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [floorSupport, setFloorSupport] = useState('');
  const [floorSupportError, setFloorSupportError] = useState('');
  const [showForm, setShowForm] = useState(false);
  // #587 — the Team-Lead name already recorded to Call History this session, so a
  // re-click of "Update" with the same name doesn't append a duplicate row.
  const recordedTlNameRef = useRef('');

  // CCF-6 #1 / issue #143 — the floor-support / team-lead full name is a
  // LOCAL-ONLY field (it is never sent to the Bank gateway); it is a
  // call-handling assurance that a supervisor is listening. It must accept a
  // real person's name and nothing else: letters (including accented Irish
  // letters — á é í ó ú etc., both cases), spaces, the apostrophe (O'Brien)
  // and the hyphen (Smith-Jones). Digits and every other symbol (@ { } ( ) +
  // , . / # & * ; : …) are rejected, and a blank / whitespace-only entry is
  // invalid. The Unicode `\p{L}` letter class makes accented letters pass.
  const NAME_ALLOWED = /^[\p{L} '-]+$/u;
  const floorSupportValid = NAME_ALLOWED.test(floorSupport.trim());

  const [mobile, setMobile] = useState('');
  const [mobileError, setMobileError] = useState('');

  // #254 — best-effort prefill for the "New Mobile update change template"
  // (Drive "SCRN 26"). The customer name/CIF/card come from the identified
  // customer in context; the new mobile number is filled once the agent has
  // entered one. `cardNumber` is masked to last-4 by the template's builder — a
  // full PAN must never appear in a mailto body. Everything else on the template
  // (who listened to the call, the change reason) is left blank for the agent.
  const emailTemplateContext: TemplateContext = {
    customerName: customer.name ?? '',
    cif: customer.cif ?? '',
    cardNumber: customer.onlineCardNumber ?? '',
    // #254 — the SA-Accounts callback template (SCRN-26) records the customer's
    // Open24 (online-banking) number, which the identified customer carries as
    // `onlineCardNumber`.
    open24Number: customer.onlineCardNumber ?? '',
    newMobileNumber: mobile,
    // #637 inc 13 — carry the resolved catalogue map so the New Mobile Number
    // Update template renders any admin-reworded wording (text only).
    content: contentMap,
  };
  const [review, setReview] = useState(false);
  const [confirmOption, setConfirmOption] = useState<
    '' | 'confirmed' | 'incorrect'
  >('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  // Issue #112 — PERSISTENT inline outcome shown beside the Confirm button (the
  // gateway Code + Message on failure; a clear confirmation on success). A toast
  // alone auto-dismisses and is easy to miss (@dylan219 #80/#82/#94).
  const [submitError, setSubmitError] = useState('');

  // The contact-number gateway call replaces the numbers it receives, so we
  // must send the customer's current landline alongside the new mobile or it
  // would be cleared (CCF-6 data-loss guard). Prefill it from the dashboard and
  // fail-closed: the Confirm submit is blocked until the current details load.
  const [currentLandline, setCurrentLandline] = useState('');
  const [loaded, setLoaded] = useState(false);
  // CCF-6 #3 / issue #140: the Visa-Debit/Credit-card email reminder (the
  // CCF-63 "Draft email" section) must appear ONLY when the customer
  // actually holds such a card — the story scopes it to "If the customer has
  // Visa Debit or Credit card(s)". Derived from the dashboard accounts loaded
  // below (it is only meaningful once the customer's accounts are loaded).
  const [hasVisaOrCredit, setHasVisaOrCredit] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) return;
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/dashboard',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load the current contact details. Please retry.');
          return;
        }
        const info = data.info ?? {};
        setCurrentLandline(String(info.HomePhone ?? ''));
        // CCF-6 #3 / issue #140: decide Visa-Debit/Credit-card eligibility from
        // the accounts the dashboard already returns — no extra gateway call or
        // RBAC surface (the screen lacks the `listCards` permission). A debit
        // card on file surfaces as a non-empty `CardNumber` (PTSB debit cards
        // are Visa Debit); a credit-card account is gateway ProductType `CC`,
        // mapped to AccountType `CARD` (#75).
        const accounts = Array.isArray(data.accounts) ? data.accounts : [];
        setHasVisaOrCredit(
          accounts.some((a) => {
            const card =
              typeof a.CardNumber === 'string' ? a.CardNumber.trim() : '';
            return card !== '' || a.AccountType === 'CARD';
          }),
        );
        setLoaded(true);
      } catch {
        if (!cancelled)
          setError('Could not load the current contact details. Please retry.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  function onUpdateFloorSupport(): void {
    // The Update button is disabled while the name is invalid/blank (CCF-6 #1
    // / unhappy path #1, issue #143), so this is normally only reached for a
    // valid name; guard defensively all the same.
    if (!floorSupportValid) {
      setFloorSupportError(
        floorSupport.trim()
          ? 'Floor support / team lead name may contain only letters, spaces, apostrophes and hyphens.'
          : 'Enter the floor support / team lead name.',
      );
      return;
    }
    setFloorSupportError('');
    setShowForm(true);

    // #587 (@dylan219) — "if an agent enters a TL name for approval in Mobile
    // number update it should record in call history". Record a Call-History
    // event naming the Team Lead who was entered for approval, best-effort and
    // once per distinct name (a re-click of Update with the SAME name must not
    // append a duplicate "Call details" row). The write is authoritative
    // server-side (`/calls/tl-approval-event`), bound to the CIF in context.
    const enteredName = floorSupport.trim();
    if (
      hasContext &&
      enteredName &&
      recordedTlNameRef.current !== enteredName
    ) {
      recordedTlNameRef.current = enteredName;
      void api
        .POST('/calls/tl-approval-event', {
          // Teller-ID gated (like /calls/security-event) — the mandatory 4-digit
          // Teller ID header must be sent or it 403s and nothing records.
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, teamLeadName: enteredName },
        })
        .catch(() => {
          // Best-effort record write — never block the maintenance flow.
        });
    }
  }

  function onMobileChange(v: string): void {
    setMobile(v);
    // #773 (option B) — the number is now sent to the gateway EXACTLY as the
    // agent types it (no separator inserted, no separator stripped). The real
    // PTSB gateway REJECTS a bare-digit Irish mobile (ErrorNumber 73, #112/#324)
    // and only accepts a space/hyphen-separated national form, so the agent must
    // be able to TYPE that separator here. Allow digits plus at most one space or
    // hyphen as a group separator (mirrors the landline field's `[\d-]` rule on
    // Personal Details / SCRN-24); anything else is still rejected.
    setMobileError(v && !/^[\d\s-]*$/.test(v) ? 'Enter numbers only.' : '');
    // Editing the number after a review invalidates the review + any prior
    // submit outcome (#112 — the inline error is for the number just tried).
    setReview(false);
    setConfirmOption('');
    setSubmitError('');
  }

  function onEnter(): void {
    // 6–16 characters total (gateway cap, #324), with 6+ actual digits. The
    // value is forwarded to the gateway verbatim (#773 option B), so a separator
    // the agent typed is preserved — a bare-digit value is accepted by the field
    // but declined downstream by the gateway (the accepted trade-off, #112).
    const digits = mobile.replace(/[\s-]/g, '');
    if (!/^[\d\s-]{6,16}$/.test(mobile) || digits.length < 6) {
      setMobileError('Mobile number must be 6–16 digits.');
      return;
    }
    setMobileError('');
    setReview(true);
    setConfirmOption('');
  }

  function onConfirmOption(option: 'confirmed' | 'incorrect'): void {
    setConfirmOption(option);
    if (option === 'incorrect') {
      // Clear for re-entry.
      setMobile('');
      setReview(false);
      setConfirmOption('');
    }
  }

  async function onConfirm(): Promise<void> {
    if (!hasContext) {
      setError('No verified customer in context. Complete ID&V first.');
      return;
    }
    if (!loaded) {
      setError('Current contact details have not loaded yet. Please retry.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/customers/contact-number', {
        params: { header: { 'X-Teller-Id': tellerId } },
        // Send the current landline alongside the new mobile so the gateway
        // (which replaces the numbers it receives) does not clear it (CCF-6).
        // Since #773 (option B, Dylan 2026-09-08) the BFF forwards the mobile to
        // the gateway EXACTLY as the agent typed it — no hyphen is inserted (only
        // the `+`/E.164 safety-normalisation is applied). A bare-digit Irish
        // mobile is therefore declined by the gateway (ErrorNumber 73, #112) —
        // the accepted trade-off of "pass over exactly what is entered".
        body: {
          customerNumber,
          mobileNumber: mobile,
          ...(currentLandline ? { landline: currentLandline } : {}),
          // Issue #569 (@dylan219): the MOBILE number update requires High
          // Security — declare `updateKind: 'mobile'` so the BFF applies the
          // High-Security gate (a landline update, by contrast, needs only
          // medium). Absent/unknown kinds also fail safe to High Security.
          updateKind: 'mobile',
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248 — the RBAC *action* denial: `requirePermission` refuses a
        // blocked action (SCRN-26 `updateContactNumber`) with 403
        // `{ error: 'forbidden' }` (e.g. a restricted role attempting this write
        // on an OFFLINE journey, per increment 1). That case shows @dylan219's
        // authoritative verbatim copy inline beside the Confirm button — never a
        // raw 403 or a generic error. This is distinct from the gateway
        // business-decline (#80/#82/#112) and from the Teller-ID gate
        // (`invalid_teller_id`), which keeps its own message.
        //
        // Issue #112: otherwise surface the failure as a PERSISTENT inline error
        // beside the Confirm button showing the gateway's Code + Message
        // verbatim, so the agent can act on the real reason and retry. `apiError`
        // carries the BFF `gateway_error` body on a true 4xx; `data` carries the
        // SuccessErrorInd:'N' result on a 200.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatContactUpdateError(apiError ?? data));
        setSubmitError(message);
        toast.error(message);
        return;
      }
      setDone(true);
      toast.success('Mobile number change submitted.');
    } catch {
      const message = 'Could not update the mobile number. Please retry.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="mobile-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Email templates (#798): the SCRN-26 templates are surfaced as a
            single top-right "Email templates ▾" dropdown selector (the shared
            {@link EmailTemplateMenu}, matching every other template screen),
            offering "Mobile Number Update" and "SA Accounts Callback". Picking
            one pops the source template prefilled best-effort from the
            in-context customer, delivered as a downloadable Outlook draft
            (`.eml`, #434). Nothing is sent from the app; the agent completes +
            addresses the draft to their Team Lead. */}
        <EmailTemplateMenu
          templates={MOBILE_UPDATE_TEMPLATES}
          context={emailTemplateContext}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="mobile-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Change mobile number
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      <div className="max-w-[800px] space-y-4">
        {/* Floor support / team lead. */}
        <div>
          <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1D252C] mb-4">
            Please ensure that floor support/team lead is listening to this call
            before proceeding.
          </p>
          <div className="flex gap-4 items-start">
            <div className="w-[400px]">
              <label
                htmlFor="floor-support"
                className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
              >
                Floor support/team lead full name
              </label>
              <input
                id="floor-support"
                type="text"
                value={floorSupport}
                onChange={(e) => {
                  const v = e.target.value;
                  setFloorSupport(v);
                  // Surface the allow-list hint inline as the agent types (the
                  // Update button is independently disabled until valid), and
                  // clear it once the entry is valid or empty (issue #143).
                  setFloorSupportError(
                    v.trim() && !NAME_ALLOWED.test(v.trim())
                      ? 'Floor support / team lead name may contain only letters, spaces, apostrophes and hyphens.'
                      : '',
                  );
                }}
                placeholder="Enter full name"
                aria-invalid={!!floorSupportError}
                className={`w-full px-4 py-2 border rounded-md bg-white font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 ${
                  floorSupportError ? 'border-[#dc2626]' : 'border-[#d1d5db]'
                }`}
              />
              <div className="min-h-[18px]">
                {floorSupportError && (
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    role="alert"
                  >
                    {floorSupportError}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onUpdateFloorSupport}
              disabled={!floorSupportValid}
              className="mt-[28px] px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update
            </button>
          </div>
        </div>

        {/* Mobile form. */}
        {showForm && (
          <div className="max-w-[700px] p-8 border-2 border-[#dad7d3] bg-white">
            <div className="flex gap-4 items-start">
              <div className="w-[400px]">
                <label
                  htmlFor="new-mobile"
                  className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                >
                  New mobile number — enter numbers only
                </label>
                <input
                  id="new-mobile"
                  type="text"
                  inputMode="numeric"
                  value={mobile}
                  onChange={(e) => onMobileChange(e.target.value)}
                  maxLength={16}
                  placeholder="Enter mobile number"
                  aria-invalid={!!mobileError}
                  className={`w-full px-4 py-2 border rounded-md bg-white font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 ${
                    mobileError ? 'border-[#dc2626]' : 'border-[#d1d5db]'
                  }`}
                />
                <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] mt-1 m-0">
                  For Irish mobile numbers use format 0850000000; for
                  international use 00440000000000 (no + prefix).
                </p>
                <div className="min-h-[18px]">
                  {mobileError && (
                    <p
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                      role="alert"
                    >
                      {mobileError}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onEnter}
                disabled={mobile.replace(/[\s-]/g, '').length < 6}
                className="mt-[28px] px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Enter
              </button>
            </div>

            {review && !done && (
              <>
                <div className="border-t border-[#dad7d3] my-8" />
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1D252C]">
                  Can I confirm the following number is correct:{' '}
                  <span className="font-bold">{mobile}</span>
                </p>
                <div className="flex items-center gap-6 mt-4">
                  <label className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer">
                    <input
                      type="radio"
                      name="mobile-confirmation"
                      checked={confirmOption === 'confirmed'}
                      onChange={() => onConfirmOption('confirmed')}
                      className="size-[18px] accent-[#FF703E]"
                    />
                    Mobile number confirmed
                  </label>
                  <label className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer">
                    <input
                      type="radio"
                      name="mobile-confirmation"
                      checked={confirmOption === 'incorrect'}
                      onChange={() => onConfirmOption('incorrect')}
                      className="size-[18px] accent-[#FF703E]"
                    />
                    Mobile number incorrect
                  </label>
                  {confirmOption === 'confirmed' && (
                    <button
                      type="button"
                      disabled={submitting || !loaded}
                      onClick={() => void onConfirm()}
                      className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting
                        ? 'Working…'
                        : loaded
                          ? 'Confirm'
                          : 'Loading…'}
                    </button>
                  )}
                </div>

                {/* Issue #112 — persistent inline failure beside the Confirm
                    button. Shows the gateway's Code + Message verbatim (e.g.
                    "73 — Unable to Process Transaction") so the agent can
                    correct the number and retry without the change being
                    silently lost (@dylan219 #80/#82/#94). */}
                {submitError && (
                  <div
                    id="mobile-update-error"
                    role="alert"
                    aria-live="assertive"
                    className="mt-4 max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                  >
                    {submitError}
                  </div>
                )}
              </>
            )}

            {done && (
              <>
                <div className="border-t border-[#dad7d3] my-8" />
                <p
                  className="text-[16px] font-['Arial',sans-serif] text-[#1D252C] leading-relaxed"
                  role="alert"
                  aria-live="polite"
                >
                  Your change request has now been sent to processing.
                  <br />
                  <br />
                  You will not be able to perform any action that requires a
                  text message or push notification until the mobile updates
                  within the next 24 hours.
                </p>
              </>
            )}
          </div>
        )}

        {/* Visa/TSYS reminder after confirm — shown ONLY when the customer
            holds a Visa Debit or Credit card (CCF-6 #3 / issue #140). For a
            customer with neither, the draft-email affordance does not apply, so
            only the 24-hour processing notice above is shown. */}
        {done && hasVisaOrCredit && (
          // Issue #664 follow-up (@philipc39): restore the ORANGE "Please note"
          // advisory box. The prior fix (MR !985) correctly made "Draft Email"
          // a heading but flattened this box to a plain grey border, so it lost
          // its orange advisory identity. Use the standard PTSB orange-accent
          // advisory pattern (cream fill + orange left rule, as on Card
          // Maintenance / Funds Transfer).
          <div className="max-w-[600px] p-4 bg-[#F8EEE3] border-l-4 border-[#FF703E] mt-4">
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1D252C] mb-2">
              Please note
            </p>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1D252C] leading-relaxed m-0">
              If the customer has a Visa Debit or Credit card(s) please fill out
              the Mobile Number Update email draft and send to a Team Leader to
              update on TSYS.
            </p>
            {/* Issue #664 (CCRS-3451) — "Draft email" is a section HEADER, not a
                clickable control. The template affordance below it is the shared
                "Email templates ▾" dropdown ({@link EmailTemplateMenu}), offering
                "Mobile Number Update" and "SA Accounts Callback"; the standalone
                "Draft email" button was a redundant/confusing affordance and is
                now a plain heading. Issue #798 — the confirmation-step template
                affordance is the same dropdown as the header row (was a button
                row), so both surfaces match every other template screen. */}
            <div className="mt-4">
              <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1D252C] mb-3">
                Draft email
              </h3>
              <EmailTemplateMenu
                templates={MOBILE_UPDATE_TEMPLATES}
                context={emailTemplateContext}
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
